import styled, { css } from 'styled-components';
import {
    ColorNeutralWhite,
    ColorOpacityDark3,
    Spacing8,
    Spacing16,
    Spacing24,
    Spacing48,
    Spacing112
} from 'autogen/design-tokens/tokens';
import {
    type ConnectedContentInlineModel,
    type ConnectedContentProductDisplayModel,
    type SurveyQuestionResponseModel,
    SurveyResponseCreate
} from 'autogen/swagger/all';
import { useSurveyResponseUpdateMutation } from 'autogen/swagger/all/hooks';
import LoadingContainer from 'components/checkout/components/BasketOverview/components/LoadingContainer';
import { resolveImageLoading } from 'components/connectedContent/shared/helpers';
import Card from 'components/generic/card';
import Picture from 'components/generic/picture';
import mapResponsiveImageModelToPictureProps from 'components/generic/picture/helpers/mappingHelper';
import Tag from 'components/generic/tag';
import { Typography } from 'components/generic/typography';
import type { IHeadline } from 'components/generic/typography/components/Headline';
import Flex from 'components/Layout/components/Flex';
import type { FlexProperties } from 'components/Layout/components/Flex/Flex';
import { breakpoint, sizes } from 'variables';
import { useOpenSurveyQuickView } from '../shared/hooks/useOpenSurveyQuickView';
import { ButtonChip } from './SurveyQuickSingleAnswer';

const StyledCard = styled(Card)<{
    $widthType: ConnectedContentInlineModel['widthType'];
    $type: ConnectedContentSurveyType;
}>`
    position: relative;
    overflow: hidden;

    ${({ $type }) =>
        $type === 'ConnectedContentInline' &&
        css`
            border-radius: unset;
            grid-column: span 2; // always span two on mobile if type inlineConnectedContent
        `}

    ${breakpoint.up(sizes.sm)} {
        grid-column: ${({ $widthType }) => ($widthType === 'Two' ? 'span 2' : 'span 1')};
    }

    .preview--mobile-small &,
    .preview--mobile-large &,
    .preview--desktop-small &,
    .preview--desktop-large & {
        border-radius: unset;
    }
`;

const StyledButtonChip = styled(ButtonChip)`
    flex-grow: 1;
`;

const StyledLoadingContainer = styled(LoadingContainer)<{ $widthType: ConnectedContentInlineModel['widthType'] }>`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: ${Spacing48} ${Spacing24};

    ${breakpoint.up(sizes.sm)} {
        padding: ${({ $widthType }) =>
            $widthType === 'Two' ? `${Spacing48} ${Spacing112};` : `${Spacing48} ${Spacing24};`};
    }

    .preview--mobile-small &,
    .preview--mobile-large & {
        padding: ${Spacing48} ${Spacing24};
    }

    .preview--desktop-small &,
    .preview--desktop-large & {
        width: ${({ $widthType }) => ($widthType === 'Two' ? '100%' : '284px')};
    }
`;

const StyledPicture = styled(Picture)`
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    display: flex;
    justify-content: center;
    flex: 1 1 0;
    height: 100%;
    width: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${ColorOpacityDark3};
    }
`;

const SurveyContainer = styled.div`
    color: ${ColorNeutralWhite};
    display: flex;
    flex-direction: column;
    gap: ${Spacing16};
    z-index: 1;
    width: 100%;
`;

const StyledTag = styled(Tag)`
    align-self: flex-start;
`;

type ConnectedContentSurveyType = 'ConnectedContentInline' | 'ConnectedContentProductDisplay';

const widthSettings: Record<
    ConnectedContentInlineModel['widthType'],
    { title: IHeadline['size']; direction: FlexProperties['direction'] }
> = {
    One: { title: 'M', direction: { xs: 'row', sm: 'column' } },
    Two: { title: 'L', direction: 'row' }
};

type ConnectedContentSurveyItemProps = {
    type: 'ConnectedContentInline' | 'ConnectedContentProductDisplay';
    model: ConnectedContentInlineModel | ConnectedContentProductDisplayModel;
};

export default function ConnectedContentSurveyItem({ model, type }: ConnectedContentSurveyItemProps) {
    const { surveyQuestion, dataLayer, image } = model;
    const { openSurveyQuickViewBySurveyModel } = useOpenSurveyQuickView();
    const { mutateAsync: SurveyResponseUpdate, isLoading } = useSurveyResponseUpdateMutation();

    if (!surveyQuestion?.firstQuestion) return null;

    let widthType: ConnectedContentInlineModel['widthType'] = 'One';
    if ('widthType' in model) widthType = model.widthType;

    let placement: ConnectedContentInlineModel['placement'] | undefined;
    if ('placement' in model) placement = model.placement;

    const createSurveyAndSubmitAnswer = async (surveyQuestionResponse: SurveyQuestionResponseModel) => {
        try {
            const initialSurveyModel = await SurveyResponseCreate({
                surveyId: surveyQuestion.surveyId,
                returnUrl: window.location.href
            });
            const data = await SurveyResponseUpdate({
                requestBody: {
                    surveyId: initialSurveyModel.surveyId,
                    id: initialSurveyModel.responseId,
                    currentPageId: initialSurveyModel.currentPage.id,
                    questionResponses: [surveyQuestionResponse]
                }
            });

            if (!data.isSuccess) {
                throw new Error(`Unknown error: ${data.errorMessage}`);
            }

            if (!data.surveyResponse && data.redirectUrl) {
                // complete the survey and redirect if there's no response and a redirect
                window.location.href = data.redirectUrl;
            }

            if (data.surveyResponse) {
                openSurveyQuickViewBySurveyModel(data.surveyResponse);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // a temporary safeguard to avoid displaying too many answer options in connected content.
    // old surveys can sometimes have multiple answers on the first page, and should be corrected by matas.
    const firstTwoAnswers = surveyQuestion.firstQuestion.answerOptions.slice(0, 2);

    return (
        <StyledCard $type={type} $widthType={widthType} data-datalayer={JSON.stringify(dataLayer)}>
            <StyledLoadingContainer $widthType={widthType} isLoading={isLoading}>
                <SurveyContainer>
                    {model.heading && <StyledTag text={model.heading} />}
                    <Typography.Headline size={widthSettings[widthType].title}>
                        {surveyQuestion.firstQuestion.title}
                    </Typography.Headline>
                    <Flex direction={widthSettings[widthType].direction} gap={Spacing8} wrap="wrap">
                        {firstTwoAnswers.map((answer) => (
                            <StyledButtonChip
                                $size="lg"
                                $variant="primaryFilled"
                                key={answer.id}
                                onClick={async () => {
                                    await createSurveyAndSubmitAnswer({
                                        questionId: surveyQuestion.firstQuestion.id,
                                        id: 0,
                                        isSelectAllChosen: false,
                                        answerOptionResponses: [
                                            {
                                                answerOptionId: answer.id,
                                                dynamicId: answer.dynamicData?.id || '',
                                                value: ''
                                            }
                                        ]
                                    });
                                }}
                            >
                                {answer.title}
                            </StyledButtonChip>
                        ))}
                    </Flex>
                </SurveyContainer>

                <StyledPicture
                    {...mapResponsiveImageModelToPictureProps(image, widthType === 'Two' ? 220 * 2 + 24 : 220)}
                    loading={resolveImageLoading(image, placement)}
                />
            </StyledLoadingContainer>
        </StyledCard>
    );
}
